// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { getAwardsVerificationRequest, getVerificationRequest } from "../slices/publicSlice/certificationVerification";
import { useAppDispatch, RootState, useAppSelector } from "../slices/store";
import HeaderImage from "../assets/img/certificate-background.png";
import OverlayImage from "../assets/img/headeroverlaycertificate.png";
import {
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { certificationBadgeLink, contactDetails } from "../config/constant";
import { useCandidateContact } from "../utils/useCandidateContact";

const AwardedCertificates = () => {
  let { certificationId } = useParams();
  const dispatch = useAppDispatch();
  const appState = useAppSelector((state: RootState) => state.verification);
  const certifications = appState?.certifications;
  const awards = appState?.awards;
  const [candidate] = useCandidateContact(appState);
  useEffect(() => {
    if (certificationId) {
      dispatch(getVerificationRequest(certificationId));
      dispatch(getAwardsVerificationRequest(certificationId));
    }
  }, []);
  return (
    <>
      <Box
        component={"img"}
        sx={{
          width: "100%",
          height: { xs: "200px", md: "366px" },
          position: "relative",
          top: "0",
          textAlign: "center",
          mt: 8.2,
        }}
        src={HeaderImage}
        alt="header"
      />
      <Box
        component={"img"}
        src={OverlayImage}
        alt="overlay"
        sx={{
          position: "absolute",
          top: "5%",
          left: { xs: "26%", md: "33%" },
          width: { xs: "40%", md: "36%" },
        }}
      />
      <Container>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            width: "100%",
          }}
        >
          {appState.state === "loading" ? (
            <div style={{ width: "25%", marginTop: "20%", marginBottom: 200 }}>
              <LinearProgress color="secondary" />
            </div>
          ) : (
            <>
              {candidate  ? (
                <>
                  <Typography variant="h4" mt={5}>
                    {`${candidate.firstName} ${candidate.lastName}`}
                  </Typography>
                  <Grid sx={{ width: "100%" }}>
                    <Divider />
                  </Grid>

                  <Typography variant="body2" mt={2}>
                    has successfully met all requirements and criteria, and
                    therefore has been awarded the following WSO2 Certificates:
                  </Typography>
                  <Stack direction="row" spacing={2} mt={5}>
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      {certifications.map((certification, index) => (
                        <Grid
                          key={index}
                          item
                          {...{ xs: 12, sm: 6, md: 4, lg: 3 }}
                          minHeight={160}
                          minWidth={300}
                          sx={{
                            textAlign: "center",
                          }}
                        >
                          <Paper
                            variant="outlined"
                            sx={{
                              padding: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              component="h2"
                              sx={{ fontWeight: "bold", minHeight: 60 }}
                            >
                              {certification?.displayName
                                ? certification?.displayName
                                : certification?.name}
                            </Typography>
                            <Grid>
                              {certification.badgeLink ? (
                                <img
                                  width={150}
                                  src={certification.badgeLink}
                                  alt={certification.badgeLink}
                                />
                              ) : (
                                <img
                                  width={150}
                                  src={
                                    certificationBadgeLink[
                                      certification.name as keyof typeof certificationBadgeLink
                                    ]
                                  }
                                />
                              )}
                            </Grid>
                            <Typography variant="body2" component="h2" mt={2}>
                              Awarded Date :
                            </Typography>
                            <Typography
                              variant="body2"
                              component="h2"
                              sx={{ minHeight: 60 }}
                            >
                              {certification.awardedDate}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                      {awards.map((award, index) => (
                        <Grid
                        key={index}
                        item
                        {...{ xs: 12, sm: 6, md: 4, lg: 3 }}
                        minHeight={160}
                        minWidth={300}
                        sx={{
                          textAlign: "center",
                        }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            padding: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            component="h2"
                            sx={{ fontWeight: "bold", minHeight: 60 }}
                          >
                            {award?.name}
                          </Typography>
                          <Grid>
                            {award.logo ? (
                              <img
                                width={150}
                                src={award.logo}
                                alt={award.logo}
                              />
                            ) : (
                              <img
                                width={150}
                                src={
                                  certificationBadgeLink[
                                    award.name as keyof typeof certificationBadgeLink
                                  ]
                                }
                              />
                            )}
                          </Grid>
                          <Typography variant="body2" component="h2" mt={2}>
                            Awarded Date :  
                          </Typography>
                          <Typography
                            variant="body2"
                            component="h2"
                            sx={{ minHeight: 60 }}
                          >
                            {award.date}
                          </Typography>
                        </Paper>
                      </Grid>
                      ))}
                    </Grid>
                  </Stack>
                </>
              ) : (
                <>
                  <img
                    src="/no-data.svg"
                    alt="404"
                    height={200}
                    style={{ marginTop: 50 }}
                  />

                  <Typography variant="h6" sx={{ color: "gray", mt: 5 }}>
                    No data to show
                  </Typography>
                </>
              )}
            </>
          )}
        </Box>
      </Container>
      <Box
        component={"div"}
        mt={5}
        pt={10}
        sx={{
          width: "100%",
          height: "100px",
          position: "relative",
          bottom: "0",
          textAlign: "center",
          backgroundColor: "primary.main",
        }}
        color="#fff"
      >
        <Typography
          component={"a"}
          sx={{
            textDecoration: "none",
            color: "#fff",
            fontWeight: "bold",
            ":hover": {
              color: "secondary.main",
            },
          }}
          href={contactDetails.mailTo}
        >
          For more information contact us on certification@wso2.com
        </Typography>
        <br />
        <Typography
          component={"a"}
          sx={{
            textDecoration: "none",
            color: "#fff",
            fontWeight: "bold",
            ":hover": {
              color: "secondary.main",
            },
          }}
          href={contactDetails.certificationWebUrl}
          target={"_blank"}
        >
          or visit our site at wso2.com/training/certification
        </Typography>
      </Box>
    </>
  );
};

export default AwardedCertificates;
